import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { accessLevel, moduleForPath, officerAllowedModules, isStaffRole } from "@/lib/access";
import { inInstalledApp } from "@/components/FullscreenButton";
import { useEffect, useState } from "react";
import { Eye, LayoutDashboard, ScrollText, Monitor, UserCog, Settings, CreditCard, GraduationCap, CalendarCheck2, Crown, Sparkles, Loader2, Plug, Clock, CalendarDays, BellRing, Layers, MessageSquareText, Car, BusFront, Tv, Menu, FileSignature, HeartHandshake, ClipboardList, StickyNote, BookOpenCheck, FolderOpen, Baby, Scale, AlertTriangle, HeartPulse, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BillingBanner from "@/components/BillingBanner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import UserMenu from "@/components/UserMenu";
import { DistrictNoticeBanner } from "@/components/DistrictNoticeBanner";
import { HipaaAckBanner } from "@/components/HipaaAckBanner";

export default function AdminLayout() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [companyType, setCompanyType] = useState("business");
  const [subLocked, setSubLocked] = useState(false);
  const loc = useLocation();
  const [companyName, setCompanyName] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [companyCorporateId, setCompanyCorporateId] = useState(null);
  const [corporate, setCorporate] = useState(null); // null = unknown, false = none, object = linked
  const [brandLogo, setBrandLogo] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);
  const [excusePending, setExcusePending] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (alive) setMsgUnread(data.count || 0);
      } catch { /* not fatal */ }
      try {
        const { data } = await api.get("/excuse-requests/pending-count");
        if (alive) setExcusePending(data.count || 0);
      } catch { /* not fatal */ }
    };
    poll();
    const iv = setInterval(poll, 20000);
    window.addEventListener("logerd:excuses-updated", poll);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("logerd:excuses-updated", poll); };
  }, []);
  const [corpName, setCorpName] = useState("");
  const [corpLocations, setCorpLocations] = useState(1);
  const [corpQuote, setCorpQuote] = useState(null);
  useEffect(() => {
    if (!upgradeOpen) return;
    const n = Math.max(1, Number(corpLocations) || 1);
    const t = setTimeout(() => api.get("/billing/corporate-quote", { params: { locations: n } }).then(({ data }) => setCorpQuote(data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [corpLocations, upgradeOpen]);
  const [upgrading, setUpgrading] = useState(false);

  const loadBuilding = () => {
    api.get("/settings/building").then(({ data }) => {
      setBrandLogo(data.effective_logo_data_url || data.logo_data_url || "");
      setBuildingName(data.name || "");
    }).catch(() => {});
  };

  const loadCorporate = () => {
    api.get("/corporate/me").then(({ data }) => setCorporate(data)).catch(() => setCorporate(false));
  };

  useEffect(() => {
    api.get("/me/company").then(({ data }) => { setCompanyType(data.type || "business"); setCompanyName(data.name || ""); setCompanyCorporateId(data.corporate_id || null); setSubLocked(data.subscription_active === false); }).catch(() => {});
    loadCorporate();
    loadBuilding();
    // Refresh sidebar brand + logo when Building Settings or Corporate info is updated.
    const onBuildingUpdate = () => loadBuilding();
    const onCorporateUpdate = () => { loadCorporate(); loadBuilding(); };
    window.addEventListener("logerd:building-updated", onBuildingUpdate);
    window.addEventListener("logerd:corporate-updated", onCorporateUpdate);
    return () => {
      window.removeEventListener("logerd:building-updated", onBuildingUpdate);
      window.removeEventListener("logerd:corporate-updated", onCorporateUpdate);
    };
  }, []);


  const upgrade = async (e) => {
    e?.preventDefault();
    if (!corpName.trim() || upgrading) return;
    setUpgrading(true);
    try {
      const { data } = await api.post("/corporate/upgrade", { corporate_name: corpName.trim(), location_count: Math.max(1, Number(corpLocations) || 1) });
      setCorporate(data);
      setUpgradeOpen(false);
      toast.success(`Welcome to ${data.name} — your corporate console is ready.`);
      await refresh();
      nav("/corporate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  const isDaycare = companyType === "daycare";
  const isSchool = companyType === "school" || isDaycare;
  const isCare = companyType === "care";
  const isOfficer = isStaffRole(user);
  const allowed = isOfficer ? officerAllowedModules(user) : null;
  const can = (module) => !isOfficer || accessLevel(user, module) !== "none";
  const currentModule = moduleForPath(location.pathname);
  const readOnly = isOfficer && currentModule && accessLevel(user, currentModule) === "read";
  const isCorpLinked = !!corporate;
  const isCorpAdmin = user?.role === "corporate_admin";
  // Brand label: prefer the corporate group name (if user is corporate-linked),
  // else the building display name (what the admin updates in Building Settings),
  // else the legal company name, else the app brand.
  const brandName = corporate?.name || buildingName || companyName || "Logerd";

  useEffect(() => {
    if (subLocked && !loc.pathname.startsWith("/admin/billing")) nav("/admin/billing?locked=1", { replace: true });
  }, [subLocked, loc.pathname, nav]);

  return (
    <div className="grid h-screen grid-cols-1 md:grid-cols-[260px_1fr] bg-slate-50" data-testid="admin-layout">
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 md:hidden" data-testid="admin-mobile-overlay" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside
        onClick={(e) => { if (e.target.closest("a,button")) setMobileNavOpen(false); }}
        className={`fixed inset-y-0 left-0 z-50 w-[260px] flex flex-col border-r border-slate-200 bg-white overflow-y-auto transform transition-transform duration-200 md:static md:z-auto md:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-200">
          <div className={`flex h-10 w-10 items-center justify-center overflow-hidden flex-shrink-0 ${brandLogo ? "bg-white border border-slate-200 rounded-xl p-1" : ""}`}>
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-full w-full object-contain" data-testid="sidebar-brand-logo" />
            ) : (
              <img src="/logerd-logo.png" alt="Logerd" className="h-full w-full object-contain" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold tracking-tight text-slate-900 truncate" data-testid="sidebar-brand-name">{brandName}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {isCorpLinked ? "Corporate · " : ""}{isSchool ? "School Admin" : isCare ? "Care Home Admin" : "Admin Console"}
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {subLocked ? (
            <>
              <div className="mx-1 mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="nav-locked-notice">
                <b>Plan expired.</b> Sign-in is paused for your team until payment is made.
              </div>
              <SidebarLink to="/admin/billing" Icon={CreditCard} label="Billing" testid="nav-billing" />
            </>
          ) : (<>
          {(isCorpLinked || isCorpAdmin) && (
            <SidebarLink to="/corporate" Icon={Crown} label={corporate?.name || "Corporate"} testid="nav-corporate" />
          )}
          {!isOfficer && <SidebarLink to="/admin" end Icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />}
          {can("visits") && <SidebarLink to="/admin/log" Icon={ScrollText} label={isSchool ? "Visitor Log" : "Visit Log"} testid="nav-log" />}
          {can("notes") && <SidebarLink to="/admin/notes" Icon={StickyNote} label="Notes" testid="nav-notes" />}
          {can("files") && <SidebarLink to="/admin/files" Icon={FolderOpen} label="Files" testid="nav-files" />}
          {isCare && can("clients") && (
            <>
              <SidebarLink to="/admin/clients" Icon={HeartHandshake} label="Clients" testid="nav-clients" />
              <SidebarLink to="/admin/stays" Icon={ClipboardList} label="Stay Log" testid="nav-stays" />
            </>
          )}
          {isSchool && !isOfficer && (
            <>
              <SidebarLink to="/admin/students" Icon={isDaycare ? Baby : GraduationCap} label={isDaycare ? "Children" : "Students"} testid="nav-students" />
              {isDaycare && <SidebarLink to="/admin/daily-log" Icon={ClipboardList} label="Daily Log" testid="nav-daily-log" />}
              {isDaycare && <SidebarLink to="/admin/ratios" Icon={Scale} label="Ratios" testid="nav-ratios" />}
              {isDaycare && <SidebarLink to="/admin/incidents" Icon={AlertTriangle} label="Incidents" testid="nav-incidents" />}
              {isDaycare && <SidebarLink to="/admin/health" Icon={HeartPulse} label="Health" testid="nav-health" />}
              {isDaycare && <SidebarLink to="/admin/tuition" Icon={CreditCard} label="Tuition" testid="nav-tuition" />}
              <SidebarLink to="/admin/attendance" Icon={CalendarCheck2} label="Attendance" testid="nav-attendance" badge={excusePending} />
              <SidebarLink to="/admin/calendar" Icon={CalendarDays} label={isDaycare ? "Calendar" : "School Calendar"} testid="nav-calendar" />
              <SidebarLink to="/admin/forms" Icon={FileSignature} label="Forms" testid="nav-forms" />
              <SidebarLink to="/admin/classes" Icon={Layers} label={isDaycare ? "Rooms" : "Courses"} testid="nav-classes" />
              {!isDaycare && <SidebarLink to="/admin/report-cards" Icon={BookOpenCheck} label="Report Cards" testid="nav-report-cards" />}
              <SidebarLink to="/admin/messages" Icon={MessageSquareText} label="Messages" testid="nav-messages" badge={msgUnread} />
              <SidebarLink to="/admin/notifications" Icon={BellRing} label="Parent Alerts" testid="nav-notifications" />
              {!isDaycare && <>
              <SidebarLink to="/dismissal" Icon={Car} label="Car Riders" testid="nav-dismissal" />
              <a href="/dismissal/board" target={inInstalledApp() ? "_self" : "_blank"} rel="noreferrer"
                 className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 transition"
                 data-testid="nav-dismissal-board">
                <Tv className="h-4 w-4" />
                <span className="text-sm font-medium">Dismissal TV Board</span>
              </a>
              <SidebarLink to="/admin/buses" Icon={BusFront} label="Buses" testid="nav-buses" />
              <SidebarLink to="/admin/integrations" Icon={Plug} label="Integrations" testid="nav-integrations" />
              </>}
            </>
          )}
          {!isOfficer && <>
          <SidebarLink to="/admin/officers" Icon={UserCog} label="Employees" testid="nav-officers" />
          <SidebarLink to="/admin/timesheet" Icon={Clock} label="Timesheet" testid="nav-timesheet" />
          <SidebarLink to="/admin/settings" Icon={Settings} label="Settings" testid="nav-settings" />
          <SidebarLink to="/admin/compliance" Icon={ShieldCheck} label="Compliance" testid="nav-compliance" />
          </>}
          {!isOfficer && !isCorpLinked && !companyCorporateId && (
            <SidebarLink to="/admin/billing" Icon={CreditCard} label="Billing" testid="nav-billing" />
          )}
          <a href="/kiosk" target={inInstalledApp() ? "_self" : "_blank"} rel="noreferrer"
             className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100 transition"
             data-testid="nav-kiosk">
            <Monitor className="h-4 w-4" />
            <span className="text-sm font-medium">Open Kiosk</span>
          </a>

          {corporate === false && !companyCorporateId && user?.role === "admin" && (
            <button
              type="button"
              onClick={() => { setCorpName(""); setUpgradeOpen(true); }}
              data-testid="upgrade-to-corporate-btn"
              className="mt-4 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 transition"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">Upgrade to Corporate</span>
            </button>
          )}
          </>)}
        </nav>

      </aside>

      <main className="overflow-y-auto">
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-slate-200 px-4 py-3">
          <button onClick={() => setMobileNavOpen(true)} data-testid="admin-mobile-menu-btn"
                  className="h-9 w-9 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-700">
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-display font-bold text-slate-900 truncate flex-1">{brandName}</span>
          <UserMenu roleLabel={isOfficer ? (user?.role === "officer" ? "Officer" : "Staff") : isCorpAdmin ? "Corporate Admin" : isSchool ? "School Admin" : "Admin"} />
        </div>
        <div className="hidden md:flex justify-end px-6 pt-3" data-testid="admin-topbar">
          <UserMenu roleLabel={isOfficer ? (user?.role === "officer" ? "Officer" : "Staff") : isCorpAdmin ? "Corporate Admin" : isSchool ? "School Admin" : "Admin"} />
        </div>
        <DistrictNoticeBanner />
        <BillingBanner />
        <HipaaAckBanner />
        {isOfficer && (!currentModule || accessLevel(user, currentModule) === "none") && allowed.length > 0 && <Navigate to={allowed[0].path} replace />}
        {readOnly && (
          <div className="mx-6 md:mx-10 mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 flex items-center gap-2" data-testid="read-only-banner">
            <Eye className="h-4 w-4" /> Read-only access — you can view this area but not make changes. Ask an admin for edit rights.
          </div>
        )}
        <Outlet />
      </main>

      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" /> Upgrade to Corporate
            </DialogTitle>
            <DialogDescription>
              Manage multiple locations under one billing account. Your current location becomes the primary, and you can add unlimited sub-locations afterwards.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={upgrade} className="space-y-3 pt-2">
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Corporate / Group name</span>
              <input
                autoFocus required
                value={corpName}
                onChange={(e) => setCorpName(e.target.value)}
                placeholder="e.g. Acme Schools District"
                data-testid="upgrade-corporate-name"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                minLength={2} maxLength={160}
              />
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">How many locations will you manage?</span>
              <input
                type="number" min={1} max={1000} required
                value={corpLocations}
                onChange={(e) => setCorpLocations(e.target.value)}
                data-testid="upgrade-location-count"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
              <span className="block mt-1 text-xs text-slate-500">Including this one. You can change it later from the corporate Billing page.</span>
            </label>
            {corpQuote && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="upgrade-quote">
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-600">{corpQuote.location_count} location{corpQuote.location_count === 1 ? "" : "s"} × ${corpQuote.price_per_location_usd.toFixed(2)}</span>
                  <span className="font-semibold text-slate-900">${(corpQuote.location_count * corpQuote.price_per_location_usd).toFixed(2)}</span>
                </div>
                {corpQuote.free_locations > 0 && (
                  <div className="flex items-baseline justify-between text-emerald-700" data-testid="upgrade-quote-free">
                    <span>Volume discount · {corpQuote.free_locations} location{corpQuote.free_locations === 1 ? "" : "s"} free</span>
                    <span className="font-semibold">−${(corpQuote.free_locations * corpQuote.price_per_location_usd).toFixed(2)}</span>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-slate-200 flex items-baseline justify-between">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-display text-xl font-bold text-slate-900" data-testid="upgrade-quote-total">${corpQuote.monthly_total_usd.toFixed(2)}<span className="text-xs font-normal text-slate-500"> / month</span></span>
                </div>
                <div className="text-right text-xs text-slate-500">or ${corpQuote.annual_total_usd.toFixed(2)} / year</div>
                {(() => { const next = corpQuote.free_tiers.find((t) => t.min_locations > corpQuote.location_count); return next ? <div className="mt-1.5 text-[11px] text-slate-500" data-testid="upgrade-quote-next-tier">Reach {next.min_locations} locations and {next.free_locations} are free.</div> : null; })()}
              </div>
            )}
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setUpgradeOpen(false)} data-testid="upgrade-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={upgrading || corpName.trim().length < 2} data-testid="upgrade-confirm" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {upgrading && <Loader2 className="h-4 w-4 animate-spin" />} Create corporate account
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SidebarLink({ to, end, Icon, label, testid, badge = 0 }) {
  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition ${
          isActive ? "bg-primary/10 text-primary" : "text-slate-700 hover:bg-slate-100"
        }`}>
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium flex-1">{label}</span>
      {badge > 0 && (
        <span data-testid={`${testid}-badge`} className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}
